import * as THREE from "three";
import type { Arrow } from "../entities/projectiles";

interface ArrowVisual {
  readonly group: THREE.Group;
  readonly materials: THREE.MeshBasicMaterial[];
}

const WOOD_COLOR = new THREE.Color(0x8a6233);
const FLINT_COLOR = new THREE.Color(0x404044);
const FEATHER_COLOR = new THREE.Color(0xe0e0e0);

export class ProjectileRenderer {
  readonly group = new THREE.Group();

  private readonly visuals = new Map<number, ArrowVisual>();
  private readonly shaftGeo = new THREE.BoxGeometry(0.04, 0.04, 0.6);
  private readonly tipGeo = new THREE.BoxGeometry(0.08, 0.08, 0.12);
  private readonly fletchGeo = new THREE.BoxGeometry(0.01, 0.12, 0.14);

  constructor(parent: THREE.Object3D) {
    this.group.name = "projectiles";
    parent.add(this.group);
  }

  sync(arrows: readonly Arrow[], light: number): void {
    const seen = new Set<number>();
    const brightness = Math.max(0.1, light);

    for (const arrow of arrows) {
      seen.add(arrow.id);
      let visual = this.visuals.get(arrow.id);
      if (!visual) {
        visual = this.createVisual();
        this.visuals.set(arrow.id, visual);
        this.group.add(visual.group);
      }

      visual.group.position.copy(arrow.position);
      visual.group.rotation.set(0, 0, 0);
      visual.group.rotation.y = arrow.yaw;
      visual.group.rotation.x = arrow.pitch;

      for (const mat of visual.materials) {
        mat.color.copy(mat.userData.baseColor).multiplyScalar(brightness);
      }
    }

    for (const [id, visual] of this.visuals) {
      if (seen.has(id)) continue;
      this.disposeVisual(visual);
      this.visuals.delete(id);
    }
  }

  dispose(): void {
    for (const visual of this.visuals.values()) {
      this.disposeVisual(visual);
    }
    this.visuals.clear();
    this.group.removeFromParent();
    this.shaftGeo.dispose();
    this.tipGeo.dispose();
    this.fletchGeo.dispose();
  }

  private createVisual(): ArrowVisual {
    const group = new THREE.Group();
    const materials: THREE.MeshBasicMaterial[] = [];

    const createMat = (color: THREE.Color): THREE.MeshBasicMaterial => {
      const mat = new THREE.MeshBasicMaterial({ color: color.clone(), fog: true });
      mat.userData = { baseColor: color.clone() };
      materials.push(mat);
      return mat;
    };

    // Shaft along Z axis
    const shaft = new THREE.Mesh(this.shaftGeo, createMat(WOOD_COLOR));
    group.add(shaft);

    // Tip at front (+Z)
    const tip = new THREE.Mesh(this.tipGeo, createMat(FLINT_COLOR));
    tip.position.z = 0.32;
    group.add(tip);

    // Feathers at back (-Z) - vertical and horizontal crosses
    const fletchV = new THREE.Mesh(this.fletchGeo, createMat(FEATHER_COLOR));
    fletchV.position.z = -0.24;
    group.add(fletchV);

    const fletchH = new THREE.Mesh(this.fletchGeo, createMat(FEATHER_COLOR));
    fletchH.position.z = -0.24;
    fletchH.rotation.z = Math.PI / 2;
    group.add(fletchH);

    return { group, materials };
  }

  private disposeVisual(visual: VisualLike): void {
    visual.group.removeFromParent();
    for (const mat of visual.materials) {
      mat.dispose();
    }
  }
}

interface VisualLike {
  readonly group: THREE.Group;
  readonly materials: THREE.MeshBasicMaterial[];
}
